import React, { useState, useEffect, useRef } from "react"
import { useSnackbar } from 'notistack';
import MenuItem from '@mui/material/MenuItem';
import axiosPrivate from "../../../api/axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { EventTabs } from '../../../utils/constants';
import { Box, Button, CircularProgress, InputAdornment, Paper, Switch, Tab, Tabs, TextField, Typography } from "@mui/material";
import ToggleSection from './ToggleSection'
import styles from './RegistrationForm.module.css';

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#0074c8" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#0074c8"
  },
};

const RegistrationForm = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const registrationFields = [
    { name: 'First Name', value: 'BPAttendeeFN' },
    { name: 'Last Name', value: 'BPAttendeeLN' },
    { name: 'Email', value: 'BPAttendeeEmail' },
    { name: 'Phone', value: 'BPAttendeePhone' },
    { name: 'Address', value: 'BPAttendeeAddress' },
    { name: 'Address 2', value: 'BPAttendeeAddress2' },
    { name: 'City', value: 'BPAttendeeCity' },
    { name: 'State', value: 'BPAttendeeState' },
    { name: 'Zip', value: 'BPAttendeeZip' },
    { name: 'Church', value: 'BPAttendeeChurch' }
  ];

  const [profileSectionsList, setProfileSectionsList] = useState([]);
  const [certificationsList, setCertificationsList] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const [registration, setFields] = useState({
    BPEventID: 0,
    RegistrationName: '',
    RegistrationCost: 0.0,
    RequireSignIn: false,
    RequireBGCheck: false,
    RequireApproval: false,
    AllowTeams: false,
    MaxTeamSize: 0,
    AllowGroups: false,
    MaxGroupSize: 0,
    SectionLabel: 'Leader',
    RegistrationType: '',
    RegistrationFields: [],
    ProfileSections: [],
    RegistrationAllowDeposit: false,
    RegistrationDepositAmount: 0,
    RegistrationDepositDueDays: 0,
    RegistrationSKU: 0,
    RegistrationCapacity: 0,
    RegistrationAllowOverCapacity: false,
    RegistrationDescription: '',
    HasDependencies: false,
    Dependency: 0,
    RequireSupplementalInsurance: false,
    BGCheckType: 0,
    RequireSignature: false,
    ProjectRegistrationType: '',
    RegistrationEnabled: false,
    SupplementalInsuranceType: '',
    HasMembership: false,
    AssociatedMembership: '',
    HasCertification: false,
    AssociatedCertification: null,
    RegistrationHasDates: false,
    RegistrationStartDate: null,
    RegistrationEndDate: null,
    RegistrationRequiredCost: false,
    RegistrationRequiredCostName: '',
    RegistrationRequiredPrice: 0.0,
    RegistrationRequiredDescription: '',
    WaiverDR: false,
    WaiverProject: false,
    RegistrationItinerary: '',
    RegistrationRequiredCostDeposit: 0.0,
    RegistrationOrder: 0
  });

  // Registration types available for each event type
  const registrationTypesByEventType = {
    Project: [
      { value: 'Leader', label: 'Leader' },
      { value: 'Member', label: 'Member' },
    ],

    'Disaster Relief': [
      { value: 'Volunteer', label: 'Volunteer' },
    ],

    Camp: [
      { value: 'Chaperone', label: 'Chaperone' },
      { value: 'Camper', label: 'Camper' },
    ],

    Program: [
      { value: 'Chaperone', label: 'Chaperone' },
      { value: 'Camper', label: 'Camper' },
      { value: 'Leader', label: 'Leader' },
      { value: 'Member', label: 'Member' },
      { value: 'General', label: 'General' },
    ],

    General: [
      { value: 'General', label: 'General' },
    ],
  };

  const [contentLoaded, setContentLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const isSubmittingRef = useRef(false);

  const [error, setError] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [insurance, setInsurance] = useState([]);

  const { registrationid, eventid } = useParams();

  const isEditMode = Boolean(registrationid);

  const eventType = eventData?.BP_Type || '';

  //easily configure groups 
  const allowGroupsForEvent =
  ["General", "Camp", "Program"].includes(eventType);

  const allowTeamsForEvent = 
  ["Project"].includes(eventType);

  /*
   * Load supporting lists.
   */
  useEffect(() => {
    let cancelled = false;

    const loadSupportingLists = async () => {
      try {
        const [profileRes, insuranceRes] = await Promise.allSettled([
          axiosPrivate.get('/api/basic/BPProfile_Sections'),
          axiosPrivate.get('/api/insurance')
        ]);

        if (cancelled) return;

        if (profileRes.status === 'fulfilled') {
          setProfileSectionsList(profileRes.value.data);
          //console.log('Profile Sections: ',profileRes.value.data);
        } else {
          console.error(
            'Error fetching profile sections:',
            profileRes.reason
          );
        }

        if (insuranceRes.status === 'fulfilled') {
          setInsurance(insuranceRes.value.data);
        } else {
          console.error(
            'Error fetching insurance list:',
            insuranceRes.reason
          );
        }
      } catch (err) {
        if (!cancelled) {
          console.error(
            'Error fetching supporting lists:',
            err
          );
        }
      }
    };

    loadSupportingLists();

    return () => {
      cancelled = true;
    };
  }, []);

  /*
   * Load the existing registration only when editing.
   */
  useEffect(() => {
    let cancelled = false;

    const loadRegistration = async () => {

      // CREATE MODE
      if (!isEditMode) {
        setFields(prev => ({
          ...prev,
          BPEventID: Number(eventid)
        }));

        setIsLoading(false);
        setContentLoaded(true);

        return;
      }

      // EDIT MODE
      try {
        setIsLoading(true);
        setError(false);

        const response = await axiosPrivate.get(
          `/api/registrations/${registrationid}`
        );

        if (cancelled) return;

        const data = response.data || {};

        setFields(prev => ({
          ...prev,
          ...data,

          BPEventID: Number(eventid),

          ProfileSections: Array.isArray(data.ProfileSections)
            ? data.ProfileSections
            : data.ProfileSections
              ? data.ProfileSections.split(',').filter(Boolean)
              : [],

          RegistrationFields: Array.isArray(data.RegistrationFields)
            ? data.RegistrationFields
            : data.RegistrationFields
              ? data.RegistrationFields.split(',').filter(Boolean)
              : []
        }));

      } catch (err) {

        if (!cancelled) {
          console.error(
            'Error fetching registration:',
            err
          );

          setError(true);

          enqueueSnackbar(
            'Failed to load registration details',
            { variant: 'error' }
          );
        }

      } finally {

        if (!cancelled) {
          setIsLoading(false);
          setContentLoaded(true);
        }
      }
    };

    loadRegistration();

    return () => {
      cancelled = true;
    };

  }, [
    isEditMode,
    registrationid,
    eventid,
    enqueueSnackbar
  ]);

  /*
   * Fetch event details for the header and
   * registration type filtering.
   */
  useEffect(() => {

    if (!eventid) return;

    let cancelled = false;

    const run = async () => {

      try {

        const resp = await axiosPrivate.get(
          `/api/events/${eventid}`
        );

        if (!cancelled) {
          setEventData(resp.data);
        }

      } catch (e) {

        // Non-critical.
        // Header will show fallback text.

      }
    };

    run();

    return () => {
      cancelled = true;
    };

  }, [eventid]);

  /*
   * In create mode, select the first registration type
   * allowed for the event type.
   */
  useEffect(() => {

    if (isEditMode || !eventType) return;

    const availableTypes =
      registrationTypesByEventType[eventType] || [];

    if (availableTypes.length === 0) return;

    setFields(prev => {

      const currentIsValid = availableTypes.some(
        type => type.value === prev.SectionLabel
      );

      if (currentIsValid) {
        return prev;
      }

      return {
        ...prev,
        SectionLabel: availableTypes[0].value
      };

    });

  }, [isEditMode, eventType]);

  /*
   * Fetch available certification types.
   */
  useEffect(() => {

    let cancelled = false;

    const run = async () => {

      try {

        const resp = await axiosPrivate.get(
          '/api/certification/types/gettypes'
        );

        if (!cancelled) {
          setCertificationsList(
            Array.isArray(resp.data)
              ? resp.data
              : []
          );
        }

      } catch (e) {

        // Non-critical.

      }
    };

    run();

    return () => {
      cancelled = true;
    };

  }, []);

  const handleRegistrationFieldsChange = e => {
    setFields(prev => ({
      ...prev,
      RegistrationFields: e.target.value
    }));
  };

  const handleProfileSectionsChange = e => {
    setFields(prev => ({
      ...prev,
      ProfileSections: e.target.value
    }));
  };

  function handleCheckboxChange(e) {

    const {
      id,
      checked
    } = e.target;

    setFields(prev => ({
      ...prev,
      [id]: checked
    }));
  }

  function handleFieldChange(e) {

    const {
      value,
      name
    } = e.target;

    setFields(prev => ({
      ...prev,
      [name]: value
    }));
  }

  function handleIntChange(e) {

    const {
      value,
      name
    } = e.target;

    setFields(prev => ({
      ...prev,
      [name]: value === ''
        ? ''
        : parseInt(value, 10)
    }));
  }

  function handleFloatChange(e) {

    const {
      value,
      name
    } = e.target;

    setFields(prev => ({
      ...prev,
      [name]: value === ''
        ? ''
        : parseFloat(value)
    }));
  }

  function handleSelectChange(e) {
  const { value, name } = e.target;


  setFields(prev => ({
    ...prev,
    [name]: value
  }));
}

  function handleTypeChange(e) {

    const { value } = e.target;

    setFields(prev => ({
      ...prev,
      SectionLabel: value
    }));
  }
//handle the tab change
 const handleTabChange = (_, newValue) => {
  const tab = EventTabs.find(t => t.name === newValue);

  if (!tab || !eventid) {
    return;
  }

  navigate(`/admin/events/${tab.path}/${eventid}`);
};

  function isUUID(str) {

    const uuidPattern =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    return uuidPattern.test(str);
  }

  /*
   * CREATE / EDIT submit handler.
   */
  const handleSubmit = async e => {

    e.preventDefault();

    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSaving(true);

    const newRegistration = {
      ...registration,
      BPEventID: Number(eventid)
    };

    const registrationTypeNum =
      parseInt(
        newRegistration.RegistrationType,
        10
      );

    /*
     * Registration fields / profile sections
     */
    if (registrationTypeNum === 1) {

      newRegistration.RegistrationFields =
        Array.isArray(
          newRegistration.RegistrationFields
        )
          ? newRegistration.RegistrationFields.join(',')
          : newRegistration.RegistrationFields;

      newRegistration.ProfileSections = null;

    } else if (registrationTypeNum === 0) {

      newRegistration.ProfileSections =
        Array.isArray(
          newRegistration.ProfileSections
        )
          ? newRegistration.ProfileSections.join(',')
          : newRegistration.ProfileSections;

      newRegistration.RegistrationFields = null;

    } else {

      newRegistration.ProfileSections = null;
      newRegistration.RegistrationFields = null;

    }

    /*
     * Membership
     */
    if (!isUUID(newRegistration.AssociatedMembership)) {
      newRegistration.AssociatedMembership = null;
    }

    /*
     * Certification
     */
    newRegistration.AssociatedCertification =
      newRegistration.HasCertification &&
      newRegistration.AssociatedCertification
        ? Number(
            newRegistration.AssociatedCertification
          )
        : null;

    try {

      /*
       * EDIT
       */
      if (isEditMode) {

        const {
          RegistrationID,
          ...updatePayload
        } = newRegistration;

        await axiosPrivate.put(
          `/api/registrations/${registrationid}`,
          updatePayload
        );

        enqueueSnackbar(
          'Registration Updated',
          { variant: 'success' }
        );

      }

      /*
       * CREATE
       */
      else {

        const {
          RegistrationID,
          ...createPayload
        } = newRegistration;

        await axiosPrivate.post(
          '/api/registrations',
          createPayload
        );

        enqueueSnackbar(
          'Registration Created',
          { variant: 'success' }
        );
      }

      navigate(
        `/admin/events/Registration/${eventid}`
      );

    } catch (saveErr) {

      console.error(
        'Failed to save registration:',
        saveErr
      );

      enqueueSnackbar(
        `Failed to ${
          isEditMode
            ? 'update'
            : 'create'
        } registration: ${
          saveErr?.response?.data?.error ||
          saveErr.message
        }`,
        { variant: 'error' }
      );

      isSubmittingRef.current = false;
      setIsSaving(false);
    }
  };

  const availableRegistrationTypes =
    registrationTypesByEventType[eventType] || [];

 const urlSegment =
   location.pathname.split("/")[3]?.toLowerCase() || "general";
 
 const activeTabName =
   (
     EventTabs.find(
       t => t.path.toLowerCase() === urlSegment
     ) ||
     EventTabs[0]
   ).name;

  const headerTitle = isEditMode
    ? `Edit Registration: ${
        registration?.RegistrationName ||
        eventData?.BP_Title ||
        ''
      }`
    : 'Create Registration';

  if (isLoading) {

    return (
      <main>

        <div className="full-row light-blue">

          <div className="container-fluid">

            <div className="row text-white pt-3 pb-3">

              <div className="col-md-12 text-white">

                <h1 className="text-uppercase">
                  {
                    isEditMode
                      ? 'Edit Registration'
                      : 'Create Registration'
                  }
                </h1>

                <p>
                  {
                    isEditMode
                      ? 'Manage attendee registration details'
                      : 'Create a new registration type'
                  }
                </p>

              </div>

            </div>

          </div>

        </div>

        <div className="full-row white">

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              gap: 2
            }}
          >

            <CircularProgress />

            <Typography>
              {
                isEditMode
                  ? 'Loading registration...'
                  : 'Preparing registration form...'
              }
            </Typography>

          </Box>

        </div>

      </main>
    );
  }

  if (error) {

    return (
      <main>

        <div className="full-row light-blue">

          <div className="container-fluid">

            <div className="row text-white pt-3 pb-3">

              <div className="col-md-12 text-white">

                <h1 className="text-uppercase">
                  {
                    isEditMode
                      ? 'Edit Registration'
                      : 'Create Registration'
                  }
                </h1>

                <p>
                  {
                    isEditMode
                      ? 'Manage attendee registration details'
                      : 'Create a new registration type'
                  }
                </p>

              </div>

            </div>

          </div>

        </div>

        <div className="full-row white">

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              gap: 2
            }}
          >

            <Typography color="error">
              Unable to load this registration.
              Please try again or contact an admin.
            </Typography>

            <Button
              variant="outlined"
              onClick={() =>
                navigate(
                  `/admin/events/Registration/${eventid}`
                )
              }
            >
              Back to Registrations
            </Button>

          </Box>

        </div>

      </main>
    );
  }

  return (
    <main>

      {/* Blue Header Banner */}
      <div className="full-row light-blue">

        <div className="container-fluid">

          <div className="row text-white pt-3 pb-3">

            <div className="col-md-12 text-white">

              <h1 className="text-uppercase">
                {headerTitle}
              </h1>

              <p>
                {isEditMode
                  ? 'Manage attendee registration details'
                  : 'Create a new registration type'}
              </p>

            </div>

          </div>

        </div>

      </div>

      {/* Content Area */}
      <div className="full-row white">

        {/* Sticky Tab Bar */}
        <Box
          sx={{
            position: 'sticky',
            top: 40,
            zIndex: 100,
            backgroundColor: 'white',
            borderBottom: 1,
            borderColor: 'divider',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}
        >

          <Box sx={{ px: 3 }}>

            <Tabs
  value={activeTabName}
  onChange={handleTabChange}
  variant="scrollable"
  scrollButtons="auto"
  allowScrollButtonsMobile
  TabIndicatorProps={{
    sx: {
      height: 3,
      borderRadius: 2,
      backgroundColor: "#0074c8"
    }
  }}
  sx={{
    minHeight: 44,
    "& .MuiTabs-flexContainer": {
      gap: 1
    },
    "& .MuiTab-root": {
      textTransform: "none",
      fontFamily: "proxima-nova, sans-serif",
      fontWeight: 600,
      fontSize: { xs: 14, sm: 16 },
      minHeight: 44,
      px: 2,
      borderRadius: 1,
      whiteSpace: "nowrap",
      color: "text.primary",

      "&:hover": {
        backgroundColor: "rgba(0,0,0,0.06)"
      },

      "&.Mui-selected": {
        color: "#0074c8"
      }
    }
  }}
>
  {EventTabs.map((t) => (
    <Tab
      key={t.id}
      value={t.name}
      label={t.label || t.name}
      wrapped={false}

      onClick={() => {
        // Registration tab is special:
        // clicking it while already on Registration
        // should return to the registration list.
        if (
          t.name === "Registration" &&
          eventid
        ) {
          navigate(`/admin/events/registration/${eventid}`);
        }
      }}
    />
  ))}
</Tabs>

          </Box>

        </Box>

        <Box sx={{ p: 3 }}>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2
            }}
          >

            <form onSubmit={handleSubmit}>

              {/* Section 1: Basic information */}
              <div className={styles.formSection}>

                <h2 className={styles.sectionTitle}>
                  Basic Information
                </h2>

                <div className={styles.sectionDescription}>
                  Define the core details and settings
                  for this registration type
                </div>

                <div className="row">

                  <div className="col-md-6">

                    <div className={styles.fieldGroup}>

                      <TextField
                        select
                        label="Registration Type"
                        required
                        id="SectionLabel"
                        name="SectionLabel"
                        value={
                          registration?.SectionLabel || ""
                        }
                        onChange={handleTypeChange}
                        fullWidth
                        size="small"
                        helperText={
                          eventType
                            ? `Registration types available for ${eventType} events`
                            : "Select a registration type"
                        }
                        InputLabelProps={{ shrink: true }}
                      >

                        {availableRegistrationTypes.map(
                          (type) => (

                            <MenuItem
                              key={type.value}
                              value={type.value}
                            >
                              {type.label}
                            </MenuItem>

                          )
                        )}

                      </TextField>

                    </div>

                  </div>

                  <div className="col-md-6">

                    <div className={styles.fieldGroup}>

                      <TextField
                        label="Registration Name"
                        required
                        id="RegistrationName"
                        name="RegistrationName"
                        inputProps={{
                          maxLength: 200
                        }}
                        fullWidth
                        size="small"
                        placeholder="e.g., Adult Leader Registration"
                        value={
                          registration?.RegistrationName || ''
                        }
                        onChange={handleFieldChange}
                        helperText="This name will be displayed on the registration page"
                        InputLabelProps={{ shrink: true }}
                      />

                    </div>

                  </div>

                  <div className="col-md-12">

                    <div className={styles.fieldGroup}>

                      <TextField
                        label="Description"
                        required
                        id="RegistrationDescription"
                        name="RegistrationDescription"
                        fullWidth
                        multiline
                        minRows={6}
                        maxRows={8}
                        size="small"
                        placeholder="Brief description of this registration type"
                        value={
                          registration?.RegistrationDescription || ''
                        }
                        onChange={handleFieldChange}
                        helperText="A clear description helps attendees choose the right registration"
                        InputLabelProps={{ shrink: true }}
                      />

                    </div>

                  </div>

                </div>

              </div>

              {/* Section 2: Pricing & capacity */}
              <div className={styles.formSection}>

                <h2 className={styles.sectionTitle}>
                  Pricing & Capacity
                </h2>

                <div className={styles.sectionDescription}>
                  Set pricing, deposits, and capacity limits
                </div>

                <div className="row">

                  <div className="col-md-4">

                    <div className={styles.fieldGroup}>

                      <TextField
                        label="Registration Cost"
                        required
                        id="RegistrationCost"
                        name="RegistrationCost"
                        type="number"
                        inputProps={{
                          step: "0.01"
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              $
                            </InputAdornment>
                          )
                        }}
                        fullWidth
                        size="small"
                        placeholder="0.00"
                        value={
                          registration?.RegistrationCost ?? ''
                        }
                        onChange={handleFloatChange}
                        helperText="Enter the cost"
                        InputLabelProps={{ shrink: true }}
                      />

                    </div>

                  </div>

                  <div className="col-md-4">

                    <div className={styles.fieldGroup}>

                      <TextField
                        label="Capacity"
                        required
                        id="RegistrationCapacity"
                        name="RegistrationCapacity"
                        type="number"
                        fullWidth
                        size="small"
                        placeholder="100"
                        value={
                          registration?.RegistrationCapacity ?? ''
                        }
                        onChange={handleIntChange}
                        helperText="Maximum number of registrants allowed"
                        InputLabelProps={{ shrink: true }}
                      />

                    </div>

                  </div>

                  <div className="col-md-4">

                    <div className={styles.fieldGroup}>

                      <TextField
                        label="Display Order"
                        required
                        id="RegistrationOrder"
                        name="RegistrationOrder"
                        type="number"
                        fullWidth
                        size="small"
                        placeholder="1"
                        value={
                          registration?.RegistrationOrder ?? ''
                        }
                        onChange={handleIntChange}
                        helperText="Lower numbers appear first"
                        InputLabelProps={{ shrink: true }} 
                      />

                    </div>

                  </div>

                </div>

                {/* Deposit Options */}
                <ToggleSection
                  id="RegistrationAllowDeposit"
                  checked={
                    registration?.RegistrationAllowDeposit ?? false
                  }
                  onChange={handleCheckboxChange}
                  title="Allow Partial Payment (Deposit)"
                  description="Registrants can pay a deposit instead of the full amount"
                  styles={styles}
                  switchSx={switchSx}
                >

                  <div className="row">

                    <div className="col-md-6">

                      <TextField
                        label="Deposit Amount"
                        required
                        id="RegistrationDepositAmount"
                        name="RegistrationDepositAmount"
                        type="number"
                        inputProps={{
                          step: "0.01"
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              $
                            </InputAdornment>
                          )
                        }}
                        fullWidth
                        size="small"
                        value={
                          registration?.RegistrationDepositAmount ?? ''
                        }
                        onChange={handleFloatChange}
                        helperText="Enter amount required for a deposit"
                        InputLabelProps={{ shrink: true }}
                      />

                    </div>

                    <div className="col-md-6">

                      <TextField
                        label="Deposit Due (Days Before Event)"
                        id="RegistrationDepositDueDays"
                        name="RegistrationDepositDueDays"
                        type="number"
                        fullWidth
                        size="small"
                        placeholder="Enter number of days before event start"
                        value={
                          registration?.RegistrationDepositDueDays ?? ''
                        }
                        onChange={handleIntChange}
                        helperText="(Optional): Balance due days prior to event"
                        InputLabelProps={{ shrink: true }}
                      />

                    </div>

                  </div>

                </ToggleSection>

                {/* Additional Required Cost */}
                <ToggleSection
                  id="RegistrationRequiredCost"
                  checked={
                    registration?.RegistrationRequiredCost || false
                  }
                  onChange={handleCheckboxChange}
                  title="Additional Required Cost"
                  description="Add mandatory fees like team fees or materials"
                  styles={styles}
                  switchSx={switchSx}
                >

                  <div className="row">

                    <div className="col-md-6">

                      <TextField
                        label="Cost Name"
                        required
                        id="RegistrationRequiredCostName"
                        name="RegistrationRequiredCostName"
                        fullWidth
                        size="small"
                        placeholder="e.g., Team Fee"
                        helperText="Required Cost Name"
                        value={
                          registration?.RegistrationRequiredCostName || ''
                        }
                        onChange={handleFieldChange}
                        InputLabelProps={{ shrink: true }}
                      />

                    </div>

                    <div className="col-md-6">

                      <TextField
                        label="Amount"
                        required
                        id="RegistrationRequiredPrice"
                        name="RegistrationRequiredPrice"
                        helperText="Price of Required Cost"
                        type="number"
                        inputProps={{
                          step: "0.01"
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              $
                            </InputAdornment>
                          )
                        }}
                        fullWidth
                        size="small"
                        value={
                          registration?.RegistrationRequiredPrice ?? ''
                        }
                        onChange={handleFloatChange}
                        InputLabelProps={{ shrink: true }}
                      />

                    </div>

                    {/* <div className="col-md-6">

                      <TextField
                        label="Deposit Portion (Optional)"
                        id="RegistrationRequiredCostDeposit"
                        name="RegistrationRequiredCostDeposit"
                        type="number"
                        inputProps={{
                          step: "0.01"
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              $
                            </InputAdornment>
                          )
                        }}
                        fullWidth
                        size="small"
                        helperText="Required Cost Deposit Amount"
                        value={
                          registration?.RegistrationRequiredCostDeposit ?? ''
                        }
                        onChange={handleFloatChange}
                      />

                    </div> */}

                    {/* <div className="col-md-6">

                      <TextField
                        label="Description"
                        id="RegistrationRequiredDescription"
                        name="RegistrationRequiredDescription"
                        fullWidth
                        helperText="Required Cost Description"
                        size="small"
                        value={
                          registration?.RegistrationRequiredDescription || ''
                        }
                        onChange={handleFieldChange}
                      />

                    </div> */}

                  </div>

                </ToggleSection>

              </div>

              {/* Section 3: Registration Settings */}
              <div className={styles.formSection}>

                <h2 className={styles.sectionTitle}>
                  Registration Settings
                </h2>

                <div className={styles.sectionDescription}>
                  Configure availability and registration rules
                </div>

                <div className={styles.settingsGrid}>

                  {/* Enable Registration */}
                  <div className={styles.toggleCard}>

                    <div className={styles.toggleHeader}>

                      <Switch
                        id="RegistrationEnabled"
                        checked={
                          registration?.RegistrationEnabled || false
                        }
                        onChange={handleCheckboxChange}
                        size="small"
                        sx={switchSx}
                      />

                      <label
                        htmlFor="RegistrationEnabled"
                        className={styles.toggleLabel}
                      >

                        <span className={styles.toggleTitle}>
                          Enable Registration
                        </span>

                        <span className={styles.toggleDescription}>
                          Allow people to register for this type
                        </span>

                      </label>

                    </div>

                  </div>

                  {/* Allow Over Capacity */}
                  <div className={styles.toggleCard}>

                    <div className={styles.toggleHeader}>

                      <Switch
                        id="RegistrationAllowOverCapacity"
                        checked={
                          registration?.RegistrationAllowOverCapacity || false
                        }
                        onChange={handleCheckboxChange}
                        size="small"
                        sx={switchSx}
                      />

                      <label
                        htmlFor="RegistrationAllowOverCapacity"
                        className={styles.toggleLabel}
                      >

                        <span className={styles.toggleTitle}>
                          Allow Over Capacity
                        </span>

                        <span className={styles.toggleDescription}>
                          Accept registrations beyond the capacity limit
                        </span>

                      </label>

                    </div>

                  </div>

                  {/* Require Approval */}
                  <div className={styles.toggleCard}>

                    <div className={styles.toggleHeader}>

                      <Switch
                        id="RequireApproval"
                        checked={
                          registration?.RequireApproval || false
                        }
                        onChange={handleCheckboxChange}
                        size="small"
                        sx={switchSx}
                      />

                      <label
                        htmlFor="RequireApproval"
                        className={styles.toggleLabel}
                      >

                        <span className={styles.toggleTitle}>
                          Require Approval
                        </span>

                        <span className={styles.toggleDescription}>
                          Registrations must be manually approved
                        </span>

                      </label>

                    </div>

                  </div>

                  {/* Allow Groups */}
                  { allowGroupsForEvent && (

                    <div className={styles.toggleCard}>

                      <div className={styles.toggleHeader}>

                        <Switch
                          id="AllowGroups"
                          checked={
                            registration?.AllowGroups || false
                          }
                          onChange={handleCheckboxChange}
                          size="small"
                          sx={switchSx}
                        />

                        <label
                          htmlFor="AllowGroups"
                          className={styles.toggleLabel}
                        >

                          <span className={styles.toggleTitle}>
                            Allow Groups
                          </span>

                          <span className={styles.toggleDescription}>
                            Enable group registrations
                          </span>

                        </label>

                      </div>

                      {registration?.AllowGroups && (

                        <div className="row px-3 pb-2 pt-2">

                          <div className="col-md-12">

                            <TextField
                              type="number"
                              label="Max Group Size"
                              size="small"
                              fullWidth
                              id="MaxGroupSize"
                              name="MaxGroupSize"
                              value={
                                registration?.MaxGroupSize ?? ''
                              }
                              onChange={handleIntChange}
                              inputProps={{ min: 1 }}
                              helperText="Maximum number of people in a group"
                              InputLabelProps={{ shrink: true }} 
                            />

                          </div>

                        </div>

                      )}

                    </div>

                  )}

                  {/* Allow Teams */}
                  { allowTeamsForEvent && (

                    <div className={styles.toggleCard}>

                      <div className={styles.toggleHeader}>

                        <Switch
                          id="AllowTeams"
                          checked={
                            registration?.AllowTeams || false
                          }
                          onChange={handleCheckboxChange}
                          size="small"
                          sx={switchSx}
                        />

                        <label
                          htmlFor="AllowTeams"
                          className={styles.toggleLabel}
                        >

                          <span className={styles.toggleTitle}>
                            Allow Teams
                          </span>

                          <span className={styles.toggleDescription}>
                            Enable team registrations
                          </span>

                        </label>

                      </div>

                      {registration?.AllowTeams && (

                        <div className="row px-3 pb-2 pt-2">

                          <div className="col-md-12">

                            <TextField
                              type="number"
                              label="Max Team Size"
                              size="small"
                              fullWidth
                              id="MaxTeamSize"
                              name="MaxTeamSize"
                              value={
                                registration?.MaxTeamSize ?? ''
                              }
                              onChange={handleIntChange}
                              inputProps={{ min: 1 }}
                              helperText="Maximum number of people on a team"
                              InputLabelProps={{ shrink: true }}
                            />

                          </div>

                        </div>

                      )}

                    </div>

                  )}

                </div>

              </div>
                            {/* Section 4: Registration Dates */}
              {/* <div className={styles.formSection}>

                <h2 className={styles.sectionTitle}>
                  Registration Dates
                </h2>

                <div className={styles.sectionDescription}>
                  Configure when this registration is available
                </div>

                <ToggleSection
                  id="RegistrationHasDates"
                  checked={
                    registration?.RegistrationHasDates || false
                  }
                  onChange={handleCheckboxChange}
                  title="Limit Registration Dates"
                  description="Set specific start and end dates for registration"
                  styles={styles}
                  switchSx={switchSx}
                >

                  <div className="row">

                    <div className="col-md-6">

                      <TextField
                        label="Registration Start Date"
                        id="RegistrationStartDate"
                        name="RegistrationStartDate"
                        type="date"
                        fullWidth
                        size="small"
                        InputLabelProps={{
                          shrink: true
                        }}
                        value={
                          registration?.RegistrationStartDate || ''
                        }
                        onChange={handleFieldChange}
                      />

                    </div>

                    <div className="col-md-6">

                      <TextField
                        label="Registration End Date"
                        id="RegistrationEndDate"
                        name="RegistrationEndDate"
                        type="date"
                        fullWidth
                        size="small"
                        InputLabelProps={{
                          shrink: true
                        }}
                        value={
                          registration?.RegistrationEndDate || ''
                        }
                        onChange={handleFieldChange}
                      />

                    </div>

                  </div>

                </ToggleSection>

              </div> */}

              {/* Section 5: Attendee Requirements */}
              <div className={styles.formSection}>

                <h2 className={styles.sectionTitle}>
                  Attendee Requirements
                </h2>

                <div className={styles.sectionDescription}>
                  Configure information and requirements for registrants
                </div>

                <div className={styles.settingsGrid}>

                  {/* Require Sign In */}
                  {/* <div className={styles.toggleCard}>

                    <div className={styles.toggleHeader}>

                      <Switch
                        id="RequireSignIn"
                        checked={
                          registration?.RequireSignIn || false
                        }
                        onChange={handleCheckboxChange}
                        size="small"
                        sx={switchSx}
                      />

                      <label
                        htmlFor="RequireSignIn"
                        className={styles.toggleLabel}
                      >

                        <span className={styles.toggleTitle}>
                          Require Sign In
                        </span>

                        <span className={styles.toggleDescription}>
                          Require attendees to sign in before registering
                        </span>

                      </label>

                    </div>

                  </div> */}

                  {/* Background Check */}
                  <div className={styles.toggleCard}>

                    <div className={styles.toggleHeader}>

                      <Switch
                        id="RequireBGCheck"
                        checked={
                          registration?.RequireBGCheck || false
                        }
                        onChange={handleCheckboxChange}
                        size="small"
                        sx={switchSx}
                      />

                      <label
                        htmlFor="RequireBGCheck"
                        className={styles.toggleLabel}
                      >

                        <span className={styles.toggleTitle}>
                          Require Background Check
                        </span>

                        <span className={styles.toggleDescription}>
                          Require a background check for registrants
                        </span>

                      </label>

                    </div>

                    {registration?.RequireBGCheck && (

                      <div className="row px-3 pb-2 pt-2">

                        <div className="col-md-6">

                          <TextField
                            select
                            label="Background Check Type"
                            id="BGCheckType"
                            name="BGCheckType"
                            fullWidth
                            size="small"
                            value={
                              registration?.BGCheckType ?? 0
                            }
                            onChange={handleSelectChange}
                            InputLabelProps={{ shrink: true }}
                          >

                            <MenuItem value={0}>
                              BOM Conducted
                            </MenuItem>

                            <MenuItem value={1}>
                              Self Conducted
                            </MenuItem>

                          </TextField>

                        </div>

                      </div>

                    )}

                  </div>

                  {/* Require Signature */}
                  {/* <div className={styles.toggleCard}>

                    <div className={styles.toggleHeader}>

                      <Switch
                        id="RequireSignature"
                        checked={
                          registration?.RequireSignature || false
                        }
                        onChange={handleCheckboxChange}
                        size="small"
                        sx={switchSx}
                      />

                      <label
                        htmlFor="RequireSignature"
                        className={styles.toggleLabel}
                      >

                        <span className={styles.toggleTitle}>
                          Require Signature
                        </span>

                        <span className={styles.toggleDescription}>
                          Require registrants to provide a signature
                        </span>

                      </label>

                    </div>

                  </div> */}

                  {/* Supplemental Insurance */}
                  <div className={styles.toggleCard}>

                    <div className={styles.toggleHeader}>

                      <Switch
                        id="RequireSupplementalInsurance"
                        checked={
                          registration?.RequireSupplementalInsurance || false
                        }
                        onChange={handleCheckboxChange}
                        size="small"
                        sx={switchSx}
                      />

                      <label
                        htmlFor="RequireSupplementalInsurance"
                        className={styles.toggleLabel}
                      >

                        <span className={styles.toggleTitle}>
                          Require Supplemental Insurance
                        </span>

                        <span className={styles.toggleDescription}>
                          Require supplemental insurance information
                        </span>

                      </label>

                    </div>

                    {registration?.RequireSupplementalInsurance && (

                      <div className="row px-3 pb-2 pt-2">

                        <div className="col-md-6">

                          <TextField
                            select
                            label="Insurance Type"
                            id="SupplementalInsuranceType"
                            name="SupplementalInsuranceType"
                            fullWidth
                            size="small"
                            value={
                              registration?.SupplementalInsuranceType || ''
                            }
                            onChange={handleSelectChange}
                            InputLabelProps={{ shrink: true }}
                          >

                            {insurance.map((item) => (

                              <MenuItem
                                key={
                                  item.id 
                                }
                                value={
                                  item.id.toString()
                                }
                              >
                                {
                                  item.label
                                }
                              </MenuItem>

                            ))}

                          </TextField>

                        </div>

                      </div>

                    )}

                  </div>

                </div>

              </div>

                   {/* Section 7: Certification */}
<div>
  {registration?.SectionLabel === "Volunteer" && (
    <div className={styles.formSection}>
    <h2 className={styles.sectionTitle}>Certification</h2>

  <div className={styles.sectionDescription}>
    Configure certification requirements
  </div>
    <div className={styles.settingsGrid}>

      {/* Certification */}
      <div className={styles.toggleCard}>

        <div className={styles.toggleHeader}>

          <Switch
            id="HasCertification"
            checked={
              registration?.HasCertification || false
            }
            onChange={handleCheckboxChange}
            size="small"
            sx={switchSx}
          />

          <label
            htmlFor="HasCertification"
            className={styles.toggleLabel}
          >

            <span className={styles.toggleTitle}>
              Associate Certification
            </span>

            <span className={styles.toggleDescription}>
              Assign a certification during event checkin
            </span>

          </label>

        </div>

        {registration?.HasCertification && (
          <div className="row px-3 pb-2 pt-2">

            <div className="col-md-12">

              <TextField
                select
                label="Associated Certification"
                id="AssociatedCertification"
                name="AssociatedCertification"
                fullWidth
                size="small"
                value={
                  registration?.AssociatedCertification ?? ""
                }
                onChange={handleSelectChange}
                InputLabelProps={{ shrink: true }}
              >

                <MenuItem value="">
                  Select Certification
                </MenuItem>

                {certificationsList.map((cert) => (
                  <MenuItem
                    key={cert.CertificationTypeID}
                    value={cert.CertificationTypeID}
                  >
                    {cert.Name}
                  </MenuItem>
                ))}

              </TextField>

            </div>

          </div>
        )}

      </div>

    </div>
    </div>
  )}

</div>
            
              

              {/* Section 8: Waivers & Event Specific Settings */}
              <div className={styles.formSection}>

                <h2 className={styles.sectionTitle}>
                  Waivers & Event Settings
                </h2>

                <div className={styles.sectionDescription}>
                  Configure event-specific waivers and information
                </div>

                <div className={styles.settingsGrid}>

{/* Disaster Relief Waiver */}
                {registration?.SectionLabel === "Volunteer" && (
                  
                  <div className={styles.toggleCard}>

                    <div className={styles.toggleHeader}>

                      <Switch
                        id="WaiverDR"
                        checked={
                          registration?.WaiverDR || false
                        }
                        onChange={handleCheckboxChange}
                        size="small"
                        sx={switchSx}
                      />

                      <label
                        htmlFor="WaiverDR"
                        className={styles.toggleLabel}
                      >

                        <span className={styles.toggleTitle}>
                          Disaster Relief Waiver
                        </span>

                        <span className={styles.toggleDescription}>
                          Require the disaster relief waiver
                        </span>

                      </label>

                    </div>
                
                  </div>
                )}

                  {/* Project Waiver */}
                  {registration?.SectionLabel !== "Volunteer" && (
                  <div className={styles.toggleCard}>

                    <div className={styles.toggleHeader}>

                      <Switch
                        id="WaiverProject"
                        checked={
                          registration?.WaiverProject || false
                        }
                        onChange={handleCheckboxChange}
                        size="small"
                        sx={switchSx}
                      />

                      <label
                        htmlFor="WaiverProject"
                        className={styles.toggleLabel}
                      >

                        <span className={styles.toggleTitle}>
                          Project Waiver
                        </span>

                        <span className={styles.toggleDescription}>
                          Require the project waiver
                        </span>

                      </label>

                    </div>

                  </div>
                )}
                </div>
                      

                {/* Itinerary */}
                {/* <div className="row mt-3">

                  <div className="col-md-12">

                    <div className={styles.fieldGroup}>

                      <TextField
                        label="Registration Itinerary"
                        id="RegistrationItinerary"
                        name="RegistrationItinerary"
                        multiline
                        rows={5}
                        fullWidth
                        size="small"
                        value={
                          registration?.RegistrationItinerary || ''
                        }
                        onChange={handleFieldChange}
                        helperText="Optional itinerary or additional event information"
                      />

                    </div>

                  </div>

                </div> */}

              </div>


              {/* Section 6: Profile & Registration Fields */}
              <div className={styles.formSection}>

                <h2 className={styles.sectionTitle}>
                  Registration Fields
                </h2>

                <div className={styles.sectionDescription}>
                  Select the information registrants must provide
                </div>

                <div className="row">

                  <div className="col-md-6">

                    {/* Registration Form Type */}
<div className={styles.fieldGroup}>

  <TextField
    select
    label="Registration Form Type"
    id="RegistrationType"
    name="RegistrationType"
    fullWidth
    size="small"
    value={registration?.RegistrationType ?? ''}
    onChange={handleSelectChange}
    helperText="Choose what information attendees must provide when registering"
    InputLabelProps={{ shrink: true }}
  >

    <MenuItem value="2">
      None
    </MenuItem>

    <MenuItem value="0">
      Profile Form
    </MenuItem>

    <MenuItem value="1">
      Registration Fields
    </MenuItem>

  </TextField>

</div>
</div>



                  <div className="col-md-6">

                    {/* Registration Form Configuration */}

{registration?.RegistrationType === "2" && (

  <Box
    sx={{
      borderRadius: 1,
      backgroundColor: '#f5f5f5',
      border: '1px solid',
      borderColor: 'divider'
    }}
  >
    <Typography
      variant="body2"
      color="text.secondary"
    >
      <strong>No additional information required.</strong>
      {' '}
      Attendees will not have to fill out any additional
      information when signing up for this event.
    </Typography>
  </Box>

)}

{registration?.RegistrationType === "0" && (

      <div className={styles.fieldGroup}>

        <TextField
          select
          SelectProps={{
            multiple: true
          }}
          label="Profile Sections"
          id="ProfileSections"
          name="ProfileSections"
          fullWidth
          size="small"
          value={
            Array.isArray(registration?.ProfileSections)
              ? registration.ProfileSections
              : []
          }
          onChange={handleProfileSectionsChange}
          helperText="Select the profile sections attendees must complete"
          InputLabelProps={{ shrink: true }}
        >

          {profileSectionsList.map((section) => (

            <MenuItem
              key={
                section.BPSectionsID 
              }
              value={
                section.BP_SectionName
              }
            >
              {
                section.BP_SectionName || ''
              }
            </MenuItem>

          ))}

        </TextField>

      </div>


)}

{registration?.RegistrationType === "1" && (

      <div className={styles.fieldGroup}>

        <TextField
          select
          SelectProps={{
            multiple: true
          }}
          label="Registration Fields"
          id="RegistrationFields"
          name="RegistrationFields"
          fullWidth
          size="small"
          value={
            Array.isArray(registration?.RegistrationFields)
              ? registration.RegistrationFields
              : []
          }
          onChange={handleRegistrationFieldsChange}
          helperText="Select the information attendees must provide"
          InputLabelProps={{ shrink: true }}
        >

          {registrationFields.map((field) => (

            <MenuItem
              key={field.value}
              value={field.value}
            >
              {field.name}
            </MenuItem>

          ))}

        </TextField>

      </div>

)}

                  </div>

                </div>

              </div>

              {/* Form Actions */}
              <div
                className="d-flex justify-content-end gap-2 mt-4"
              >

                <Button
                  type="button"
                  variant="outlined"
                  disabled={isSaving}
                  onClick={() =>
                    navigate(
                      `/admin/events/Registration/${eventid}`
                    )
                  }
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSaving}
                  sx={{
                    backgroundColor: '#0074c8',
                    '&:hover': {
                      backgroundColor: '#005fa3'
                    }
                  }}
                >

                  {isSaving ? (
                    <>
                      <CircularProgress
                        size={20}
                        sx={{
                          mr: 1,
                          color: 'white'
                        }}
                      />

                      {isEditMode
                        ? 'Updating...'
                        : 'Creating...'}
                    </>
                  ) : (
                    isEditMode
                      ? 'Update Registration'
                      : 'Save Registration'
                  )}

                </Button>

              </div>

            </form>

          </Paper>

        </Box>

      </div>

    </main>
  );
};

export default RegistrationForm;